/**
 * Datepicker constraint computation service.
 *
 * Handles the logic for computing disabled/highlighted states
 * and upper/lower bounds for the date and time pickers.
 *
 * Replaces the complex updateDatepickerStates, getFirstDisabledDateAfterStart,
 * datepicker2DisabledDates logic from the original components.
 */
import moment from 'moment-timezone';
import { toServerMoment, getDateOf } from './time-service';
import { getEndOfDay } from './schedule-service';

/**
 * Compute the earliest allowed end date based on minimum reservation days.
 *
 * Replaces ReservationForm.getEarliestAllowedEndDate and
 * FilterForm.datepicker2DisabledDates.
 *
 * @param {Date} startDate
 * @param {number} minDays - Minimum reservation days
 * @param {string} timezone
 * @returns {Date}
 */
export function getEarliestAllowedEndDate(startDate, minDays, timezone) {
    if (minDays > 0) {
        return toServerMoment(startDate, timezone).add(minDays, 'days').toDate();
    }
    return startDate;
}

/**
 * Find the first blocked period that starts after the current rent_start.
 *
 * Replaces ReservationForm.getFirstDisabledDateAfterStart.
 *
 * @param {Date} startDate
 * @param {number} startHour
 * @param {Array} reservedDates - Array of { start, end } date strings
 * @param {string} timezone
 * @returns {moment.Moment|null} - The blocker moment, or null if none found
 */
export function getFirstBlockerAfterStart(startDate, startHour, reservedDates, timezone) {
    var rentStart = toServerMoment(startDate, timezone)
        .hours(startHour)
        .minute(0)
        .second(0);

    for (var i = 0; i < reservedDates.length; i++) {
        var blocked = reservedDates[i];
        var blockedStart = moment.tz(blocked.start, timezone);
        if (blockedStart.isAfter(rentStart)) {
            return blockedStart;
        }
    }
    return null;
}

/**
 * Compute the disabled.from date for datepicker2 based on next blocker.
 * Returns a Date (midnight of the day) or null (no upper limit).
 *
 * When the blocker starts before opening time, we subtract a day
 * so the user cannot select the blocker's day.
 *
 * @param {moment.Moment|null} nextBlocker
 * @param {number} openingTime
 * @returns {Date|null}
 */
export function computeEndDateUpperLimit(nextBlocker, openingTime) {
    if (!nextBlocker) {
        return null;
    }

    var hour = nextBlocker.toDate().getHours();
    var adjusted = hour < openingTime
        ? nextBlocker.clone().subtract(1, 'd')
        : nextBlocker.clone();

    return getDateOf(adjusted.toDate());
}

/**
 * Compute the timepicker2 'to' value based on end date and next blocker.
 *
 * If the blocker is on the same day as the end date, cap the timepicker
 * at the blocker's hour. Otherwise use the regular end-of-day.
 *
 * @param {Date} endDate
 * @param {moment.Moment|null} nextBlocker
 * @param {Object} config - ScheduleConfig
 * @returns {number}
 */
export function computeEndTimepickerTo(endDate, nextBlocker, config) {
    if (
        nextBlocker &&
        nextBlocker.isValid() &&
        nextBlocker.isSame(toServerMoment(endDate, config.timezone), 'day')
    ) {
        return parseInt(nextBlocker.hours());
    }
    return getEndOfDay(endDate, config);
}

/**
 * Calculate rental duration in days.
 *
 * Replaces FilterForm.calculateDuration.
 * days = ceil(diffInHours / 24) with DST correction.
 *
 * @param {Date} startDate
 * @param {number} startHour
 * @param {Date} endDate
 * @param {number} endHour
 * @returns {number|null} - Days, or null if inputs are invalid
 */
export function calculateDuration(startDate, startHour, endDate, endHour) {
    if (!startDate || !endDate) {
        return null;
    }

    try {
        var sHour = Math.min(23, parseInt(startHour, 10) || 0);
        var eHour = Math.min(23, parseInt(endHour, 10) || 0);

        var start = moment(startDate).hour(sHour).minute(0).second(0).millisecond(0);
        var end = moment(endDate).hour(eHour).minute(0).second(0).millisecond(0);

        if (!end.isAfter(start)) {
            return null;
        }

        var hours = end.diff(start, 'hours', true);
        var days = Math.ceil(hours / 24);

        // DST correction: if calendar day diff matches and hours are same,
        // use calendar days instead of hour-based calculation
        var dayDiff = end.clone().startOf('day').diff(start.clone().startOf('day'), 'days');
        if (dayDiff >= 1 && end.hour() === start.hour()) {
            days = dayDiff;
        }

        return days;
    } catch (e) {
        return null;
    }
}
